/**
 * Notifications feature — Ducks pattern (actions, reducer, types).
 */

import { Notification } from '@/infra/types/domain';

// ─── State Shape ───────────────────────────────────────────

export interface NotificationsState {
  items: Notification[];
  loading: boolean;
  error: string | null;
}

export const initialState: NotificationsState = {
  items: [],
  loading: false,
  error: null,
};

// ─── Action Types ──────────────────────────────────────────

const ADD_NOTIFICATION = 'notifications/addNotification';
const DISMISS_NOTIFICATION = 'notifications/dismissNotification';
const MARK_READ = 'notifications/markRead';
const SET_NOTIFICATIONS = 'notifications/setNotifications';
const SET_LOADING = 'notifications/setNotificationsLoading';
const SET_ERROR = 'notifications/setNotificationsError';

// Trigger actions
export const FETCH_NOTIFICATIONS = 'notifications/fetchNotifications';

// ─── Action Creators ───────────────────────────────────────

export function addNotification(notification: Notification) {
  return { type: ADD_NOTIFICATION, payload: notification } as const;
}

export function dismissNotification(id: string) {
  return { type: DISMISS_NOTIFICATION, payload: id } as const;
}

export function markRead(id: string) {
  return { type: MARK_READ, payload: id } as const;
}

export function setNotifications(notifications: Notification[]) {
  return { type: SET_NOTIFICATIONS, payload: notifications } as const;
}

export function setNotificationsLoading(loading: boolean) {
  return { type: SET_LOADING, payload: loading } as const;
}

export function setNotificationsError(error: string | null) {
  return { type: SET_ERROR, payload: error } as const;
}

export function fetchNotifications() {
  return { type: FETCH_NOTIFICATIONS } as const;
}

// ─── Action Type Union ─────────────────────────────────────

type NotificationsAction =
  | ReturnType<typeof addNotification>
  | ReturnType<typeof dismissNotification>
  | ReturnType<typeof markRead>
  | ReturnType<typeof setNotifications>
  | ReturnType<typeof setNotificationsLoading>
  | ReturnType<typeof setNotificationsError>;

// ─── Reducer ───────────────────────────────────────────────

export function notificationsReducer(
  state: NotificationsState = initialState,
  action: NotificationsAction | { type: string; payload?: unknown }
): NotificationsState {
  switch (action.type) {
    case SET_NOTIFICATIONS: {
      const items = (action as ReturnType<typeof setNotifications>).payload;
      return { ...state, items };
    }

    case ADD_NOTIFICATION: {
      const notification = (action as ReturnType<typeof addNotification>).payload;
      return {
        ...state,
        items: [notification, ...state.items],
      };
    }

    case DISMISS_NOTIFICATION: {
      const id = (action as ReturnType<typeof dismissNotification>).payload;
      return {
        ...state,
        items: state.items.map((n) =>
          n.id === id
            ? { ...n, status: 'dismissed' as const, dismissed_at: new Date().toISOString() }
            : n
        ),
      };
    }

    case MARK_READ: {
      const id = (action as ReturnType<typeof markRead>).payload;
      return {
        ...state,
        items: state.items.map((n) =>
          n.id === id
            ? { ...n, status: 'read' as const, read_at: new Date().toISOString() }
            : n
        ),
      };
    }

    case SET_LOADING: {
      const loading = (action as ReturnType<typeof setNotificationsLoading>).payload;
      return { ...state, loading };
    }

    case SET_ERROR: {
      const error = (action as ReturnType<typeof setNotificationsError>).payload;
      return { ...state, error };
    }

    default:
      return state;
  }
}
